import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { API } from '../config';
import {
    CalendarIcon,
    UserIcon,
    ChevronRightIcon,
    ShareIcon,
    ArrowPathIcon,
    HeartIcon,
    ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';

const BlogPage = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchBlogs();
    }, []);

    const fetchBlogs = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/blogs`);
            setBlogs(response.data);
        } catch (error) {
            console.error('Failed to fetch blogs:', error);
            toast.error('Failed to load blog posts');
        } finally {
            setLoading(false);
        }
    };

    const handleShare = (blog) => {
        if (navigator.share) {
            navigator.share({
                title: blog.title,
                text: blog.excerpt,
                url: `${window.location.origin}/blog/${blog.id}`,
            }).catch((error) => console.log('Error sharing', error));
        } else {
            const url = `${window.location.origin}/blog/${blog.id}`;
            navigator.clipboard.writeText(url);
            toast.success('Link copied to clipboard!');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-20">
                <div className="flex flex-col items-center">
                    <ArrowPathIcon className="h-10 w-10 text-[#E31E24] animate-spin mb-4" />
                    <p className="text-gray-600 font-medium">Loading our stories...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-32 pb-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <motion.h1
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
                    >
                        Our <span className="text-[#E31E24]">Blog</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-gray-600 max-w-2xl mx-auto text-lg"
                    >
                        Stay updated with the latest news, recipes, and nutrition tips from SR Daily Nutrition.
                    </motion.p>
                </div>

                {blogs.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-gray-100">
                        <DocumentTextIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No blog posts yet</h3>
                        <p className="text-gray-500">Check back soon for new content!</p>
                    </div>
                ) : (
                    <div className="grid gap-12">
                        {blogs.map((blog, index) => (
                            <motion.article
                                key={blog.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-[2.5rem] overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 group"
                            >
                                <div className="flex flex-col md:flex-row h-full">
                                    {/* Image Container */}
                                    <div className="md:w-2/5 relative overflow-hidden aspect-[16/9] md:aspect-auto">
                                        <Link to={`/blog/${blog.id}`}>
                                            <img
                                                src={blog.image_url || 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80'}
                                                alt={blog.title}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                            />
                                        </Link>
                                        <div className="absolute top-6 left-6 flex gap-2">
                                            {blog.tags && blog.tags.slice(0, 2).map((tag, i) => (
                                                <span key={i} className="bg-white/90 backdrop-blur-sm text-[#E31E24] text-xs font-bold px-4 py-1.5 rounded-full shadow-sm">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Content Container */}
                                    <div className="md:w-3/5 p-8 md:p-12 flex flex-col">
                                        <div className="flex items-center gap-6 text-sm text-gray-500 mb-6 font-medium">
                                            <div className="flex items-center gap-2">
                                                <CalendarIcon className="h-4 w-4 text-[#E31E24]" />
                                                <span>{new Date(blog.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <UserIcon className="h-4 w-4 text-[#E31E24]" />
                                                <span>{blog.author}</span>
                                            </div>
                                            <div className="flex items-center gap-4 ml-auto font-bold">
                                                <div className="flex items-center gap-1.5">
                                                    <HeartSolid className="h-4 w-4 text-[#E31E24]" />
                                                    <span>{blog.likes?.length || 0}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <ChatBubbleLeftIcon className="h-4 w-4 text-gray-400" />
                                                    <span>{blog.comments?.length || 0}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <Link to={`/blog/${blog.id}`} className="block group/title">
                                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 group-hover/title:text-[#E31E24] transition-colors line-clamp-2">
                                                {blog.title}
                                            </h2>
                                        </Link>

                                        <p className="text-gray-600 text-lg leading-relaxed mb-8 line-clamp-3">
                                            {blog.excerpt || (blog.content.length > 200 ? blog.content.substring(0, 200) + '...' : blog.content)}
                                        </p>

                                        <div className="mt-auto flex items-center justify-between pt-8 border-t border-gray-100">
                                            <Link
                                                to={`/blog/${blog.id}`}
                                                className="inline-flex items-center text-[#E31E24] font-bold text-lg group/link"
                                            >
                                                Read More
                                                <ChevronRightIcon className="h-5 w-5 ml-2 transition-transform group-hover/link:translate-x-1" />
                                            </Link>

                                            <button
                                                onClick={() => handleShare(blog)}
                                                className="p-3 rounded-2xl bg-gray-50 text-gray-400 hover:text-[#E31E24] hover:bg-red-50 transition-all shadow-sm"
                                                title="Share this post"
                                            >
                                                <ShareIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const DocumentTextIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
);

export default BlogPage;
